/**
 * ConnectionValidator - Validation rules for ReactFlow connections
 *
 * Implements connection validation logic for Sprint-4:
 * - FR-007: Prevent self-connections (screen connecting to itself)
 * - FR-008: Prevent connections TO the launch screen
 * - FR-009: Prevent duplicate edges between the same screens
 * - FR-006: Allow valid connections between screens
 */

import { Connection, Edge } from '@xyflow/react';
import { TransitionIdGenerator } from '@modules/reactFlow/utils/converters/TransitionIdGenerator';
import type { EntityTransition } from '@modules/reactFlow/types/EntityTransition';

/**
 * Result of connection validation
 */
export interface ValidationResult {
  valid: boolean;
  errorMessage?: string;
}

/**
 * Check if connection is a self-connection (source === target)
 *
 * @param connection - ReactFlow connection or edge
 * @returns true if self-connection, false otherwise
 *
 * Related: FR-007 - System MUST prevent self-connections
 */
export function isSelfConnection(connection: Connection | Edge): boolean {
  if (!connection.source || !connection.target) {
    return false;
  }

  return connection.source === connection.target;
}

/**
 * Check if connection target is the launch screen (first screen in flow)
 *
 * @param connection - ReactFlow connection or edge
 * @param launchScreenId - ID of the launch screen
 * @returns true if target is launch screen, false otherwise
 *
 * Related: FR-008 - System MUST prevent connections TO the launch screen
 */
export function isLaunchScreenTarget(
  connection: Connection | Edge,
  launchScreenId?: string
): boolean {
  if (!connection.target || !launchScreenId) {
    return false;
  }

  // Check if target is the launch screen
  return connection.target === launchScreenId;
}

/**
 * Check if duplicate edge already exists between the same source, target AND handle (actionKey)
 *
 * FIXED: Now includes actionKey comparison to allow multiple connections between
 * the same screens through different handles.
 *
 * Examples:
 * - Screen A → Screen B via list.item.0 ✅ Allowed
 * - Screen A → Screen B via list.item.1 ✅ Allowed (different handle)
 * - Screen A → Screen B via list.item.0 ❌ Duplicate (same handle)
 *
 * @param connection - ReactFlow connection or edge (must have sourceHandle)
 * @param transitions - Array of existing transitions
 * @returns true if duplicate exists, false otherwise
 *
 * Related: FR-009 - System MUST prevent duplicate edges between same screens via same handle
 */
export function isDuplicateEdge(
  connection: Connection | Edge,
  transitions: EntityTransition[]
): boolean {
  if (!connection.source || !connection.target || !connection.sourceHandle) {
    return false;
  }

  // Parse sourceHandle to extract actionKey
  // Handle ID format: "handle:{screenId}:action:{actionKey}"
  let connectionActionKey: string;
  try {
    const parsed = TransitionIdGenerator.parseHandleId(connection.sourceHandle);
    connectionActionKey = parsed.actionKey;
  } catch (error) {
    console.warn('[ConnectionValidator] Failed to parse sourceHandle:', connection.sourceHandle, error);
    return false; // Can't validate without actionKey, allow connection
  }

  // Check if any existing transition has the same source, target AND actionKey
  return transitions.some(
    (transition) =>
      transition.sourceEntityId === connection.source &&
      transition.targetEntityId === connection.target &&
      transition.sourceActionKey === connectionActionKey
  );
}

/**
 * Validate a connection against all rules
 *
 * @param connection - ReactFlow connection or edge
 * @param transitions - Array of existing transitions (optional, for duplicate check)
 * @param launchScreenId - ID of the launch screen (optional, for launch screen check)
 * @returns ValidationResult with valid flag and error message if invalid
 *
 * Related: FR-006, FR-007, FR-008, FR-009
 */
export function validateConnection(
  connection: Connection | Edge,
  transitions?: EntityTransition[],
  launchScreenId?: string
): ValidationResult {
  // Check required parameters
  if (!connection.source || !connection.target) {
    return {
      valid: false,
      errorMessage: 'Invalid connection parameters'
    };
  }

  // FR-007: Prevent self-connections
  if (isSelfConnection(connection)) {
    return {
      valid: false,
      errorMessage: 'Cannot connect screen to itself'
    };
  }

  // FR-008: Prevent connections to launch screen
  if (isLaunchScreenTarget(connection, launchScreenId)) {
    return {
      valid: false,
      errorMessage: 'Cannot connect to launch screen'
    };
  }

  // FR-009: Prevent duplicate edges
  if (transitions && isDuplicateEdge(connection, transitions)) {
    return {
      valid: false,
      errorMessage: 'Connection already exists between these screens'
    };
  }

  // FR-006: Connection is valid
  return { valid: true };
}
